import { PersonStatusEnum } from '../Types/enums';
import { IncidentRosterPerson, PersonStatus } from '../Types/Incident';

// ── Centralized UI status labels ──

export const UI_STATUS_LABELS: Record<PersonStatus, string> = {
  notChecked: 'Not Checked',
  accounted: 'Accounted',
  notAccounted: 'Not Accounted',
};

export const HEADCOUNT_STATUS_LABELS = UI_STATUS_LABELS;

// ── Status help text / tooltip definitions ──

export const UI_STATUS_HELP = {
  notChecked: 'No headcounter action has been taken yet.',
  accounted: 'Person confirmed accounted for.',
  notAccounted: 'Person has not been accounted for.',
} as const;

/**
 * Return the person's headcount status directly from API data.
 * The API now uses only three values: notChecked, accounted, notAccounted.
 */
export const getUiPersonStatus = (person: IncidentRosterPerson): PersonStatus => {
  return person.status;
};

/** Map API status string or number to internal PersonStatus. */
export const normalizeApiPersonStatus = (raw: unknown): PersonStatus => {
  // Handle numeric values from API (0=NotChecked, 1=Accounted, 2=NotAccounted)
  if (typeof raw === 'number') {
    if (raw === PersonStatusEnum.Accounted) return 'accounted';
    if (raw === PersonStatusEnum.NotAccounted) return 'notAccounted';
    if (raw === PersonStatusEnum.NotChecked) return 'notChecked';
  }
  if (typeof raw === 'string') {
    const lower = raw.toLowerCase().replace(/[\s_-]/g, '');
    if (lower === 'accounted') return 'accounted';
    if (lower === 'notaccounted') return 'notAccounted';
    if (lower === 'notchecked') return 'notChecked';
    // Already a valid new value
    if (raw === 'accounted' || raw === 'notAccounted' || raw === 'notChecked') return raw as PersonStatus;
  }
  return 'notChecked';
};

/** Map PersonStatus to the numeric API value sent in requests. */
export const toApiPersonStatus = (status: PersonStatus): number => {
  switch (status) {
    case 'accounted': return PersonStatusEnum.Accounted;
    case 'notAccounted': return PersonStatusEnum.NotAccounted;
    case 'notChecked': return PersonStatusEnum.NotChecked;
  }
};

/** UI label for display */
export const getPersonStatusLabel = (status: PersonStatus): string => {
  return UI_STATUS_LABELS[status] ?? status;
};

/** UI label for display from raw status string (for CSV exports etc.) */
export const getStatusDisplayLabel = (status: string): string => {
  if (status in UI_STATUS_LABELS) return UI_STATUS_LABELS[status as PersonStatus];
  // Fallback normalization for any legacy values
  const normalized = normalizeApiPersonStatus(status);
  return UI_STATUS_LABELS[normalized];
};

// ── Centralized headcount status visual tokens ──
// Use these to keep row, button, chip, and KPI styling consistent.

export interface StatusVisual {
  /** Primary color (icon, text, border) */
  color: string;
  /** Softer tint background (chips, selected buttons, row accent) */
  bg: string;
  /** Very subtle row background when status is active */
  rowBg: string;
  /** Hover row background */
  rowHoverBg: string;
}

export const HEADCOUNT_STATUS_VISUALS: Record<PersonStatus, StatusVisual> = {
  accounted: {
    color: '#2E7D32', // appColors.status.success
    bg: '#E8F5E9',    // appColors.status.successBg
    rowBg: '#FFFFFF',
    rowHoverBg: '#FAFAFA',
  },
  notAccounted: {
    color: '#C62828', // dark red — readable on light rose
    bg: '#FFEBEE',    // light rose
    rowBg: '#FFFFFF',
    rowHoverBg: '#FFF8F8',
  },
  notChecked: {
    color: '#78909C', // appColors.text.caption
    bg: '#ECEFF1',    // appColors.surface.inactive
    rowBg: '#FFFFFF',
    rowHoverBg: '#FAFAFA',
  },
};
